import {
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { PermissionKey } from '../configs/permissions.config';
import { PermissionService } from '../services/permission.service';

export type AcanPermissionMode = 'hide' | 'disable' | 'mask';

/**
 * Attribute directive for permission-gated UI that can hide, disable, or mask.
 * Fail-closed: missing/empty key => denied.
 *
 * Usage:
 *   <button [acanPermission]="PERMISSION.CLIENT_ADD">Add</button>
 *   <button [acanPermission]="['A','B']" [acanPermissionEvery]="true">Need both</button>
 *   <div [acanPermission]="PERMISSION.BILLING_VIEW" acanPermissionMode="mask">...</div>
 */
@Directive({
  selector: '[acanPermission]',
  standalone: true,
})
export class AcanPermissionDirective implements OnInit, OnDestroy {
  @Input('acanPermission') key: PermissionKey | PermissionKey[] | string | string[] | null =
    null;
  @Input() acanPermissionMode: AcanPermissionMode = 'disable';
  /** When true, require ALL keys; otherwise ANY. */
  @Input() acanPermissionEvery = false;

  private sub?: Subscription;
  private originalDisplay: string | null = null;
  private appliedDisabled = false;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.sub = this.permissionService.permissionsChanged$.subscribe(() =>
      this.apply()
    );
    this.apply();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private keys(): string[] {
    if (this.key == null) return [];
    return Array.isArray(this.key) ? this.key.filter(Boolean) : [this.key].filter(Boolean);
  }

  private isAllowed(): boolean {
    const codes = this.keys();
    if (!codes.length) return false;
    return this.acanPermissionEvery
      ? this.permissionService.hasAllPermissions(codes)
      : this.permissionService.hasAnyPermission(codes);
  }

  private apply(): void {
    const allowed = this.isAllowed();
    const host = this.el.nativeElement;

    if (this.originalDisplay === null) {
      this.originalDisplay = host.style.display || '';
    }

    host.classList.remove('pp-masked');
    this.renderer.removeAttribute(host, 'aria-disabled');

    if (allowed) {
      this.renderer.setStyle(host, 'display', this.originalDisplay || null);
      if (this.appliedDisabled) {
        this.renderer.removeAttribute(host, 'disabled');
        this.appliedDisabled = false;
      }
      return;
    }

    switch (this.acanPermissionMode) {
      case 'hide':
        this.renderer.setStyle(host, 'display', 'none');
        break;
      case 'mask':
        this.renderer.setStyle(host, 'display', this.originalDisplay || null);
        host.classList.add('pp-masked');
        this.renderer.setAttribute(host, 'aria-disabled', 'true');
        break;
      case 'disable':
      default:
        this.renderer.setStyle(host, 'display', this.originalDisplay || null);
        this.renderer.setAttribute(host, 'disabled', 'true');
        this.renderer.setAttribute(host, 'aria-disabled', 'true');
        this.appliedDisabled = true;
        break;
    }
  }
}
